import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MailWarning, RefreshCw } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const GRACE_HOURS = 48;

export default function VerifyEmailBanner() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  if (!user || user.email_verified) return null;

  const start = user.created_at ? new Date(user.created_at).getTime() : Date.now();
  const remainingMs = start + GRACE_HOURS * 3600 * 1000 - Date.now();
  const expired = remainingMs <= 0;
  const hoursLeft = Math.max(1, Math.ceil(remainingMs / 3600000));

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success(t("verifyBanner.resent"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setSending(false); }
  };

  const checkVerified = async () => {
    setVerifying(true);
    try { await refresh(); } finally { setVerifying(false); }
  };

  const tone = expired
    ? "border-red-300 bg-red-50 text-red-800"
    : "border-amber-300 bg-amber-50 text-amber-900";

  return (
    <div className={`mb-6 rounded-xl border px-4 py-3.5 ${tone}`} data-testid="verify-email-banner">
      <div className="flex items-start gap-3">
        <MailWarning className="h-5 w-5 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm">{expired ? t("verifyBanner.expiredTitle") : t("verifyBanner.title")}</p>
          <p className="text-xs mt-0.5 leading-relaxed opacity-90">
            {expired ? t("verifyBanner.expiredMessage") : t("verifyBanner.message")}
          </p>
          {!expired && (
            <p className="text-[11px] font-mono mt-1 opacity-75" data-testid="verify-time-left">
              {t("verifyBanner.timeLeft", { hours: hoursLeft })}
            </p>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs bg-white/60"
            onClick={checkVerified} disabled={verifying} data-testid="verify-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 ${verifying ? "animate-spin" : ""}`} /> {t("verifyBanner.verified")}
          </Button>
          <Button size="sm" className="h-8 text-xs bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]"
            onClick={resend} disabled={sending} data-testid="verify-resend-btn">
            {sending ? t("verifyBanner.sending") : t("verifyBanner.resend")}
          </Button>
        </div>
      </div>
    </div>
  );
}
